import { useSelector } from "react-redux";
import { formatAmount, shortAmountFormat } from "utils/helper";
import { useWindowDimensions } from "hooks";
import React, { useState, useEffect } from "react";
import { Typography, Loader, Select } from "components";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import transactionVolumeImg from "assets/images/volume-chart-img.svg";
import { getTransactionStatusStats } from "redux/actions/dashboard/terminalDashboardActions";

const RegionBreakdown = () => {
  const { loading, data, dateFrom, dateTo } = useSelector((state) => ({
    loading: state.terminalDashboardReducer.transactionStatus.loading,
    data: state.terminalDashboardReducer.transactionStatus.data,
    dateFrom: state.terminalDashboardReducer.dates.dateFrom,
    dateTo: state.terminalDashboardReducer.dates.dateTo,
  }));

  const [dataToDisplay, setDataToDisplay] = useState("1");

  const { width } = useWindowDimensions();

  const isSmallScreen = width < 992;

  const totalCount =
    dataToDisplay === "1" ? data.totalTransVolume : data.totalTansValue;
  const { isError } = data || {};

  useEffect(() => {
    getTransactionStatusStats();
  }, []);

  const statuses = [
    {
      key: "PENDING_HOLD",
      label: "Pending Hold",
      color: "#0C1575",
    },
    {
      key: "COMPLETED",
      label: "Completed",
      color: "#27AE60",
    },
    {
      key: "PENDING",
      label: "Pending",
      color: "#EFB008",
    },
    {
      key: "FAILED",
      label: "Failed",
      color: "#DC2C2C",
    },
    {
      key: "PENDING_AGENT",
      label: "Pending Agent",
      color: "#1966FC",
    },
    {
      key: "PENDING_CREDIT_AGENT",
      label: "Pending Credit",
      color: "#9791CC",
    },
  ];

  const otherColors = ["#C9D6FF", "#778CA2", "#7B61FF", "#19D3FC"];

  const getChartData = () =>
    data?.data?.map(({ status, value, volumePerc, volume, valuePerc }) => {
      let currentStatus = statuses.find(
        (cStatus) => cStatus.key.toLowerCase() === status.toLowerCase()
      );

      return {
        label: currentStatus?.label || status.toLowerCase(),
        value: dataToDisplay === "1" ? volume : value,
        percent: dataToDisplay === "1" ? volumePerc : valuePerc,
        color:
          currentStatus?.color ||
          otherColors[Math.floor(Math.random() * otherColors.length)], // For unknown statuses
      };
    });

  const getColorIndicators = () =>
    getChartData()?.map(({ value, label, color, percent }, index) => (
      <div className="mt-2 d-flex align-items-center" key={index}>
        <div
          className="region-indicator-box"
          style={{ backgroundColor: color }}
        ></div>
        <div>
          <Typography variant="caption0" style={{ lineHeight: "17px" }}>
            {label} - {percent}%
          </Typography>
          <Typography variant="body4" style={{ lineHeight: "17px" }}>
            {formatAmount(value)}
          </Typography>
        </div>
      </div>
    ));

  return (
    <div className="dashboard-card transaction-success-rate pb-3">
      <div className="pt-2 px-4">
        <Typography
          variant="caption1"
          state="primary"
          className={"text-center"}
        >
          Success rate of selected transaction statuses within the set date
        </Typography>
        <div className="d-flex align-items-center justify-content-between mt-3">
          {dateFrom && dateTo && (
            <Typography variant="caption2" state="primary">
              Date set: {dateFrom} to {dateTo}
            </Typography>
          )}

          <Select
            required={false}
            placeholder=""
            value={dataToDisplay}
            onChange={(e) => setDataToDisplay(e.target.value)}
            className={"data-type-select"}
            name="dataType"
            options={[
              { label: "Volume", value: "1" },
              { label: "Value", value: "2" },
            ]}
          />
        </div>
        <div className="row">
          <div className="col-8 text-center">
            {loading ? (
              <div
                className={"text-center"}
                style={{ marginTop: "6rem", height: "13rem" }}
              >
                <Loader />
                <Typography state="grey" variant="body2">
                  Loading data...
                </Typography>
              </div>
            ) : Number(totalCount) > 0 && !isError ? (
              <div
                style={{
                  position: "relative",
                  height: "18rem",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart width={500} height={500}>
                    <Pie
                      data={getChartData()?.map((entry) => ({
                        value: entry.value,
                      }))}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius={isSmallScreen ? 55 : 70}
                      outerRadius={isSmallScreen ? 80 : 110}
                      fill="#82ca9d"
                      stroke=""
                    >
                      {getChartData()?.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            getChartData().map((entry) => entry.color)[index]
                          }
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                {totalCount && (
                  <div className={`transaction-total  text-center`}>
                    <Typography variant="subtitle1" state="primary">
                      {shortAmountFormat(totalCount)}
                    </Typography>
                    <Typography
                      variant="body2"
                      state="primary"
                      style={{ marginTop: "-.5rem" }}
                    >
                      {dataToDisplay === "1" ? "Total Volume" : "Total Value"}
                    </Typography>
                  </div>
                )}
                <div className="volume-text">
                  <Typography variant="caption1" className={"mx-4"}>
                    Total Transaction{" "}
                    {dataToDisplay === "1" ? "Volume" : "Value"}
                  </Typography>{" "}
                  <Typography state="primary" variant="body2">
                    {formatAmount(totalCount)}{" "}
                  </Typography>{" "}
                  &nbsp;
                  <img src={transactionVolumeImg} height="13px" alt="" />{" "}
                </div>
              </div>
            ) : (
              <div style={{ height: "10rem" }}>
                <Typography
                  state="grey"
                  variant="body2"
                  className={" text-center mtop-4 mbottom-4"}
                >
                  No information to display
                </Typography>
              </div>
            )}
          </div>
          <div className="col-4 pt-1">
            {!loading && totalCount && getColorIndicators()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionBreakdown;
